import * as React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { BookOpen, Link2, X } from 'lucide-react';
import apiService from '../../services/api';
import { buildPlaybookContextFromRows } from '../../utils/playbookContext';
import { directionForLanguage } from '../../i18n/config';
import './PlaybookPickerModal.css';

type PlaybookParentProject = { id: string; name: string };

/** Whole-project playbook link passed to AgentView. */
export type LinkedPlaybookSelection = {
  projectId: string;
  /** Shown on the composer chip */
  projectLabel: string;
  /** Full text sent as playbook_context (all records: author + paragraph_text). */
  contextBody: string;
  recordCount: number;
};

interface PlaybookPickerModalProps {
  preferredProjectId?: string;
  workflowHint: 'construction_loan' | 'real_estate' | undefined;
  onSelect: (selection: LinkedPlaybookSelection) => void;
  onClose: () => void;
}

export const PlaybookPickerModal: React.FC<PlaybookPickerModalProps> = ({
  preferredProjectId,
  workflowHint,
  onSelect,
  onClose,
}) => {
  const { t, i18n } = useTranslation('taskpane');
  const chromeDir = directionForLanguage(i18n.language);
  const linkedFallbackName = t('agent.playbook.linkedProject');
  const [projects, setProjects] = React.useState<PlaybookParentProject[]>([]);
  const [projectsLoading, setProjectsLoading] = React.useState(true);
  const [projectsError, setProjectsError] = React.useState<string | null>(null);

  const [activeProjectId, setActiveProjectId] = React.useState(preferredProjectId || '');
  const [linking, setLinking] = React.useState(false);
  const [linkError, setLinkError] = React.useState<string | null>(null);

  const backdropRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    setActiveProjectId(preferredProjectId || '');
  }, [preferredProjectId]);

  React.useEffect(() => {
    let cancelled = false;
    setProjectsLoading(true);
    setProjectsError(null);
    apiService
      .getConstructionLoanPlaybookParentProjects()
      .then((res) => {
        if (cancelled) return;
        if (res.success && Array.isArray(res.data)) {
          let list: PlaybookParentProject[] = res.data.map((p) => ({
            id: p.id,
            name: p.name || p.id,
          }));
          const pref = preferredProjectId?.trim();
          if (pref && !list.some((p) => p.id === pref)) {
            list = [
              { id: pref, name: linkedFallbackName },
              ...list,
            ];
          }
          list.sort((a, b) =>
            (a.name || '').localeCompare(b.name || '', i18n.language, { sensitivity: 'base' })
          );
          setProjects(list);
        } else if (preferredProjectId?.trim()) {
          setProjects([
            { id: preferredProjectId.trim(), name: linkedFallbackName },
          ]);
        } else {
          setProjectsError(res.message || t('agent.playbook.loadProjectsFailed'));
        }
      })
      .catch((e) => {
        if (cancelled) return;
        const pref = preferredProjectId?.trim();
        if (pref) {
          setProjects([{ id: pref, name: linkedFallbackName }]);
          setProjectsError(null);
        } else {
          setProjectsError(e?.message || t('agent.playbook.loadProjectsFailed'));
        }
      })
      .finally(() => {
        if (!cancelled) setProjectsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [preferredProjectId, linkedFallbackName, t, i18n.language]);

  React.useEffect(() => {
    if (projectsLoading || projects.length !== 1) return;
    setActiveProjectId((cur) => (cur ? cur : projects[0].id));
  }, [projectsLoading, projects]);

  React.useEffect(() => {
    setLinkError(null);
  }, [activeProjectId]);

  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  function handleBackdropClick(e: React.MouseEvent) {
    if (e.target === backdropRef.current) onClose();
  }

  async function handleLinkPlaybook() {
    if (!activeProjectId || linking) return;
    const project = projects.find((p) => p.id === activeProjectId);
    const projectLabel = project?.name || activeProjectId;
    setLinking(true);
    setLinkError(null);
    try {
      const rows = await apiService.listPlaybooksForAgent(activeProjectId, workflowHint);
      if (!rows.length) {
        setLinkError(t('agent.playbook.noRecords'));
        return;
      }
      const contextBody = buildPlaybookContextFromRows(projectLabel, rows);
      onSelect({
        projectId: activeProjectId,
        projectLabel,
        contextBody,
        recordCount: rows.length,
      });
      onClose();
    } catch (e: unknown) {
      setLinkError(e instanceof Error ? e.message : t('agent.playbook.loadRecordsFailed'));
    } finally {
      setLinking(false);
    }
  }

  const showProjectSelect = !projectsLoading && !projectsError && projects.length > 0;
  const canLink = Boolean(activeProjectId) && !linking && !projectsLoading;

  return (
    <div
      className="pb-modal-backdrop"
      ref={backdropRef}
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-label={t('agent.playbook.aria')}
    >
      <div className="pb-modal" dir={chromeDir}>
        <div className="pb-modal-header">
          <BookOpen size={16} aria-hidden />
          <span className="pb-modal-title">{t('agent.playbook.title')}</span>
          <button
            type="button"
            className="pb-modal-close"
            onClick={onClose}
            aria-label={t('agent.playbook.close')}
          >
            <X size={16} />
          </button>
        </div>

        <p className="pb-modal-hint">
          <Trans t={t} i18nKey="agent.playbook.hint" components={{ action: <strong /> }} />
        </p>

        <div className="pb-modal-project-row">
          <label className="pb-modal-project-label" htmlFor="pb-project-select">
            {t('agent.playbook.project')}
          </label>
          {projectsLoading ? (
            <span className="pb-modal-project-loading">{t('agent.playbook.loadingProjects')}</span>
          ) : projectsError ? (
            <span className="pb-modal-project-error">{projectsError}</span>
          ) : showProjectSelect ? (
            <select
              id="pb-project-select"
              className="pb-modal-project-select"
              value={activeProjectId}
              onChange={(e) => setActiveProjectId(e.target.value)}
              aria-label={t('agent.playbook.chooseProject')}
            >
              <option value="">{t('agent.playbook.selectProject')}</option>
              {projects.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name || p.id}
                </option>
              ))}
            </select>
          ) : (
            <span className="pb-modal-project-error">{t('agent.playbook.noPlaybookProjects')}</span>
          )}
        </div>

        {linkError && <div className="pb-modal-link-error">{linkError}</div>}

        <div className="pb-modal-actions">
          <button
            type="button"
            className="pb-link-playbook-btn"
            disabled={!canLink}
            onClick={() => void handleLinkPlaybook()}
          >
            {linking ? (
              <>{t('agent.playbook.loadingRecords')}</>
            ) : (
              <>
                <Link2 size={16} aria-hidden />
                {t('agent.playbook.title')}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
